import React, { useState, useEffect, useRef, useCallback } from "react";
import { triggerSaveIndicator } from "../../../utils/api";

/**
 * SaveIndicator — a spinner while changes are being saved, then a green check
 * when nothing is left unsaved, or a red "Not saved" when a save failed. A
 * save that got no answer stays red until that change saves, or until
 * clicked; one the server refused (its reason shown by the page) stays red
 * until the next save. Rules: utils/api.js.
 *
 * Driven by utils/api.js (triggerSaveIndicator), which counts the saves in
 * flight and calls the handlers exposed here:
 *   window.__chariotSaveIndicator?.triggerSaving()
 *   window.__chariotSaveIndicator?.triggerSaved()
 *   window.__chariotSaveIndicator?.triggerFailed()
 *
 * No context dependency — safe to place anywhere in the tree (e.g. AdminLayout top bar).
 */

const STATES = {
  IDLE: "idle",
  SAVING: "saving",
  SAVED: "saved",
  FAILED: "failed",
};

const FAILED_TITLE = "A change didn't save. Make the change again, or click to dismiss this.";

const SaveIndicator = () => {
  const [status, setStatus] = useState(STATES.IDLE);
  const savedTimer = useRef(null);

  const triggerSaving = useCallback(() => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setStatus(STATES.SAVING);
  }, []);

  const triggerSaved = useCallback(() => {
    setStatus(STATES.SAVED);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      setStatus(STATES.IDLE);
    }, 2000);
  }, []);

  const triggerFailed = useCallback(() => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setStatus(STATES.FAILED);
  }, []);

  // Expose globally so any component can trigger it
  useEffect(() => {
    window.__chariotSaveIndicator = { triggerSaving, triggerSaved, triggerFailed };
    return () => { delete window.__chariotSaveIndicator; };
  }, [triggerSaving, triggerSaved, triggerFailed]);

  useEffect(() => {
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  if (status === STATES.IDLE) return null;

  if (status === STATES.FAILED) {
    return (
      <button
        type="button"
        style={styles.failed}
        title={FAILED_TITLE}
        aria-label={FAILED_TITLE}
        onClick={() => { triggerSaveIndicator.dismissFailures(); setStatus(STATES.IDLE); }}
      >
        <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <circle cx="9" cy="9" r="8" fill="var(--status-danger-text)" />
          <path d="M9 5v5" stroke="var(--text-inverse)" strokeWidth="2" strokeLinecap="round" />
          <circle cx="9" cy="13" r="1.2" fill="var(--text-inverse)" />
        </svg>
        Not saved
      </button>
    );
  }

  return (
    <div style={styles.container} title={status === STATES.SAVING ? "Saving changes..." : "Changes saved"}>
      {status === STATES.SAVING && (
        <div style={styles.spinner}>
          <svg width="18" height="18" viewBox="0 0 18 18" style={styles.spinnerSvg}>
            <circle
              cx="9" cy="9" r="7"
              fill="none"
              stroke="var(--text-tertiary)"
              strokeWidth="2"
              strokeDasharray="30 14"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
      {status === STATES.SAVED && (
        <div style={styles.checkmark}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="8" fill="var(--status-success-text)" />
            <path
              d="M5.5 9.5L7.5 11.5L12.5 6.5"
              stroke="var(--text-inverse)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    flexShrink: 0,
  },
  spinner: {
    animation: "saveIndicatorSpin 0.8s linear infinite",
  },
  spinnerSvg: {
    display: "block",
  },
  checkmark: {
    animation: "saveIndicatorPop 0.3s ease-out",
  },
  failed: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
    padding: "4px 10px",
    border: "1px solid var(--status-danger-text)",
    borderRadius: "999px",
    background: "var(--status-danger-bg)",
    color: "var(--status-danger-text)",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    animation: "saveIndicatorPop 0.3s ease-out",
  },
};

// Inject keyframes once
if (typeof document !== "undefined") {
  const id = "save-indicator-keyframes";
  if (!document.getElementById(id)) {
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @keyframes saveIndicatorSpin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes saveIndicatorPop {
        0% { transform: scale(0); opacity: 0; }
        60% { transform: scale(1.2); }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }
}

export default React.memo(SaveIndicator);
